import axios, { type AxiosInstance } from 'axios';

import * as authApi from './auth/auth.api';
import * as customersApi from './customers/customers.api';
import * as customerUsersApi from './customerUsers/customerUsers.api';
import * as externalCostEntriesApi from './externalCostEntries/externalCostEntries.api';
import * as externalTasksApi from './externalTasks/externalTasks.api';
import * as operativesApi from './operatives/operatives.api';
import * as preliminariesApi from './preliminaries/preliminaries.api';
import * as preliminaryCostEntriesApi from './preliminaryCostEntries/preliminaryCostEntries.api';
import * as profitWarningsApi from './profitWarnings/profitWarnings.api';
import * as projectsApi from './projects/projects.api';
import * as projectTasksApi from './projectTasks/projectTasks.api';
import * as taskCostEntriesApi from './taskCostEntries/taskCostEntries.api';

function buildHybridKey(
    path: Array<string | number> | null,
    key: string | number,
) {
    const segs = [...(path ?? []), key];

    let out = '';
    for (const seg of segs) {
        if (typeof seg === 'number') {
            out += `[${seg}]`;
            continue;
        }

        // property name
        if (!out) out = seg;
        else out += `.${seg}`;
    }

    return out;
}

// Singleton axios instance - created once per apiBaseURL
let apiClientInstance: AxiosInstance | null = null;

/**
 * Initializes the singleton axios instance for the given API base URL
 * Only creates new instance if apiBaseURL changes
 */
export const initializeApiClient = (): void => {
    // If instance exists and URL hasn't changed, no need to recreate
    if (apiClientInstance) {
        return;
    }

    // Create new axios instance (not modifying global defaults)
    apiClientInstance = axios.create({
        baseURL: '/api',
        headers: {
            'Content-Type': 'application/json',
        },
        withCredentials: true,
        formSerializer: {
            visitor: function (value, key, path, helpers) {
                // Let axios recurse into objects/arrays
                if (helpers.isVisitable(value)) return true;

                const formKey = buildHybridKey(path, key);
                const converted = helpers.convertValue(value);
                this.append(formKey, converted);

                // We handled this leaf node ourselves
                return false;
            },
        },
    });

    // Set interceptors only once per instance
    apiClientInstance.interceptors.request.use(
        (config) => {
            return config;
        },
        (error) => {
            return Promise.reject(error);
        },
    );

    apiClientInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;

                if (status >= 500) {
                    error.message =
                        'An unexpected error occurred. Please try again later.';
                } else {
                    const data = error.response.data;

                    if (typeof data === 'string' && data.length > 0) {
                        error.message = data;
                    } else if (typeof data === 'object' && data !== null) {
                        if (data.message) {
                            error.message = data.message;
                        } else if (data.error) {
                            error.message = data.error;
                        } else if (data.title) {
                            error.message = data.title;
                        }
                    }
                }
            }

            return Promise.reject(error);
        },
    );
};

export const apiClient = {
    auth: authApi,
    customers: customersApi,
    customerUsers: customerUsersApi,
    projects: projectsApi,
    projectTasks: projectTasksApi,
    preliminaries: preliminariesApi,
    operatives: operativesApi,
    taskCostEntries: taskCostEntriesApi,
    preliminaryCostEntries: preliminaryCostEntriesApi,
    externalCostEntries: externalCostEntriesApi,
    externalTasks: externalTasksApi,
    profitWarnings: profitWarningsApi,
};

/**
 * Gets the current axios instance (for use in API functions)
 * Throws if instance hasn't been initialized yet
 */
export const getAxiosInstance = (): AxiosInstance => {
    if (!apiClientInstance) {
        throw new Error(
            'API client not initialized. Call initializeApiClient() first.',
        );
    }
    return apiClientInstance;
};
